import { MenuItem } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import React from 'react';
import { Control, useWatch } from 'react-hook-form';

import { Autocomplete } from '@/components/molecules/Autocomplete';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { useCountryStates } from '@/hooks/useCountryStates';
import { useStateCities } from '@/hooks/useStateCities';
import { useTranslator } from '@/hooks/useTranslator';
import { addressSchemaPartial } from '@/schemas/address';
import { CountryType } from '@/types/country';
import { NamedIdentified } from '@/types/namedIdentified';

type AddressForm = typeof addressSchemaPartial.infer;

interface Props<AddressSchema extends AddressForm> {
  readonly country: CountryType;
  readonly state: string;

  readonly extended?: boolean;
  readonly control: Control<AddressSchema>;
}

export const AddressFields = <AddressSchema extends AddressForm>(
  props: Props<AddressSchema>,
): React.ReactElement => {
  const { extended = false, country, state } = props;
  // TODO: fix this, this shouldn't be necessary
  const control = props.control as Control<AddressForm>;
  const city = useWatch({
    control: control,
    name: 'address.city',
  });

  const states = useCountryStates(country);
  const stateCities = useStateCities(country, state);
  const cities = React.useMemo((): readonly string[] => {
    return stateCities
      .map((item: NamedIdentified): string => item.name)
      .filter((item: string): boolean => item.toLowerCase().includes(city.toLowerCase()));
  }, [city, stateCities]);

  const tr = useTranslator();

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <ControllerWithError
        name="address.street"
        label={tr('STREET_ADDRESS')}
        control={control}
        render={({
          field,
        }: ControllerWithErrorParam<AddressForm, 'address.street'>): React.ReactElement => (
          <TextField variant="outlined" fullWidth={true} {...field} />
        )}
      />
      {extended && (
        <ControllerWithError
          name="address.complement"
          control={control}
          label={tr('ADDRESS_COMPLEMENT')}
          render={({
            field,
          }: ControllerWithErrorParam<AddressForm, 'address.complement'>): React.ReactElement => (
            <TextField variant="outlined" fullWidth={true} {...field} />
          )}
        />
      )}
      <Box display="flex" alignItems="start" width="100%" gap={2}>
        <ControllerWithError
          name="address.state"
          control={control}
          label={tr('STATE')}
          render={({
            field,
          }: ControllerWithErrorParam<AddressForm, 'address.state'>): React.ReactElement => (
            <TextField select={true} variant="outlined" fullWidth={true} {...field}>
              {states.map((state: NamedIdentified): React.ReactElement => {
                return (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                );
              })}
            </TextField>
          )}
        />
        <ControllerWithError
          name="address.city"
          control={control}
          label={tr('CITY')}
          render={({
            field,
          }: ControllerWithErrorParam<AddressForm, 'address.city'>): React.ReactElement => (
            <Autocomplete fullWidth={true} items={cities} {...field} />
          )}
        />
        <ControllerWithError
          name="address.zipCode"
          control={control}
          label={tr('ZIP')}
          render={({
            field,
          }: ControllerWithErrorParam<AddressForm, 'address.zipCode'>): React.ReactElement => (
            <TextField fullWidth={true} {...field} />
          )}
        />
      </Box>
    </Box>
  );
};
